import React from "react";
import {connect} from 'react-redux';
import {Link, Redirect} from "react-router-dom";

import ExtMfaShell from "./ExtMfaShell";
import ExtMfaServerError, {SERVER_ERROR_FALLBACK} from "./ExtMfaServerError";
import ExtLoader from "../../../components/ExtLoader";
import {OutlineAlert} from "../components/ExtAlerts";

import {apiErrorCode, apiErrorMessage} from "../../../services/Api";
import {MFA_ERRORS} from "../../../config/MfaConfig";
import {mfaCleared, completeMfaLogin, confirmMfaEmailLink} from "../../../redux/actions/mfaActions";

// Landing route for the emailed fallback link:
// /extranet/email-verify/:userRef/:verifyCode
//
// Anonymous by design (contract section 8) - no pending session needed and none
// consumed, so the link works in any browser. The API call is deliberately
// fired from componentDidMount rather than at module/render scope: mail-scanner
// prefetches fire on the *page* URL, so only JS that runs after a real render
// can consume this single-use token.
//
// Named-exported undecorated for unit tests; the default export below is the
// connected version used by the route.
export class ExtMfaEmailVerify extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      error: '',
      serverError: ''
    };

    this.isMounted_ = false;
  }

  componentDidMount() {
    this.isMounted_ = true;

    const userRef = this.props.match.params.userRef;
    const verifyCode = this.props.match.params.verifyCode;

    if (!userRef || !verifyCode) {
      // Dead link. Abandon any pending marker so the sign-in form below is
      // reachable instead of bouncing straight back to the MFA screens.
      this.props.clearMfa();
      this.setState({
        isLoading: false,
        error: 'That sign-in link is incomplete. Please request a new one.'
      });
      return;
    }

    confirmMfaEmailLink(userRef, verifyCode)
      .then((data) => {
        this.props.completeMfaLogin(data);
        this.props.history.replace('/extranet/dashboard');
      })
      .catch((error) => {
        const code = apiErrorCode(error);

        if (code === MFA_ERRORS.SECRET_UNREADABLE) {
          // 1028 - server-side fault. Not retryable, and the pending marker is
          // left alone: a reload is the recovery path once support fixes it.
          if (!this.isMounted_) return;
          this.setState({
            isLoading: false,
            serverError: apiErrorMessage(error, SERVER_ERROR_FALLBACK)
          });
          return;
        }

        // Any other email-link failure abandons the pending marker: a new link
        // needs a fresh password login (contract section 5). Without this the
        // "sign in again" link below lands on ExtSignIn, which bounces
        // straight back to the MFA challenge screen while a stale marker
        // exists.
        this.props.clearMfa();
        if (!this.isMounted_) return;
        let message;
        if (code === MFA_ERRORS.INVALID_CODE) {
          // 1022 - code tampered: render the server message.
          message = apiErrorMessage(error, 'The verification code in this link is not valid. Please sign in again and request a new email verification link.');
        } else if (code === MFA_ERRORS.LOCKED_OUT) {
          // 1027 - locked out; the link can't be consumed right now.
          message = apiErrorMessage(error, 'Too many incorrect attempts. Please try again in about 15 minutes.');
        } else {
          // 1025 unknown / expired / already used.
          message = apiErrorMessage(error, 'Sorry, this verification link has expired or has already been used. Please sign in again and request a new email verification link.');
        }
        this.setState({
          isLoading: false,
          error: message
        });
      });
  }

  componentWillUnmount() {
    // Async callbacks below may settle after the screen is gone (navigation,
    // refresh); don't setState then.
    this.isMounted_ = false;
  }

  render() {
    if (this.props.user.currentUser.id) {
      return <Redirect to='/extranet/dashboard' />;
    }

    return (
      <ExtMfaShell title="Signing you in">
        { this.state.isLoading && (
          <div>
            <p>Checking your link...</p>
            <ExtLoader />
          </div>
        ) }

        { !!this.state.serverError && (
          <ExtMfaServerError message={this.state.serverError} />
        ) }

        { !this.state.serverError && !this.state.isLoading && !!this.state.error && (
          <React.Fragment>
            <OutlineAlert color="danger">
              {this.state.error}
            </OutlineAlert>
            <p>
              <Link to="/extranet/auth/signin">
                Sign in with your email and password
              </Link>{' '}
              and request a new verification link from there.
            </p>
            <p>
              Or else contact <a href="//www.cabfare.com.au/contact-us">CabFare</a> for assistance.
            </p>
          </React.Fragment>
        ) }
      </ExtMfaShell>
    );
  } // render

} // ExtMfaEmailVerify

const mapStateToProps = store => ({
  user: store.user
})

const mapDispatchToProps = dispatch => ({
  clearMfa: () => dispatch(mfaCleared()),
  completeMfaLogin: (payload) => completeMfaLogin(dispatch, payload)
})

export default connect(mapStateToProps, mapDispatchToProps)(ExtMfaEmailVerify);